import React, { useState, useContext } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Card,
  CardContent,
  Typography,
  IconButton,
  Box,
  Popover,
  Button,
  styled,
  tableCellClasses,
  Badge,
  Icon,
  CircularProgress,
} from "@mui/material";
import AppContext from "../context/context";
import { isMobile } from "../../globalStyles/mobile";
import Pagination from "../pagination";
import SortingIcon from "../../globalStyles/icons/sorting-icon";
import FilterModal from "../filterModal";
import { useTheme } from "@mui/styles";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ViewIcon from "../../globalStyles/icons/vieewIcon";
import OverpackViewModal from "../overpack-view-modal";
import DeleteIcon from "../../globalStyles/icons/deleteIcon";
import DeleteConfirmationModal from "../deleteConfirmationModal";
import { shipmentService } from "../../utils/services/shipmentService";
import { toast } from "react-toastify";
import ShipmentViewModal from "../shipment-view-modal";
import { formatDate } from "../../utils/formatDate";
import UpIcon from "@mui/icons-material/ArrowUpwardOutlined";
import DownIcon from "@mui/icons-material/ArrowDownwardOutlined";
import { RefreshOutlined } from "@mui/icons-material";
import TableHeader from "../table-header";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "var(--table-header-bg)",
    border: "none",
    padding: "6px",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 12,
    padding: "6px",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: "var(--row-bg-odd)",
    border: "1px solid var(--border-color)",
  },
  "&:nth-of-type(even)": {
    backgroundColor: "var(--row-bg-even)",
    border: "1px solid var(--border-color)",
  },
}));

const Shipments = ({
  data,
  setPage,
  setRowsPerPage,
  rowsPerPage,
  page,
  dataCount,
  onChangeFilter,
  filterData,
  onDeleteData,
}) => {
  const theme = useTheme();
  const defaultColor = "#D13438";
  const primary_color = theme.palette?.primary?.main || defaultColor;
  const { user } = useContext(AppContext);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [openFilterModal, setOpenFilterModal] = useState(false);
  const [anchorElFilter, setAnchorElFilter] = useState(null);
  const [selectedfilterData, setSelectedFilterData] = useState({
    key: "",
    filter: [],
    sort: 0,
    id: "_id",
    displayKey: "name",
    fetchApi: () => {},
    loadMoreButton: false,
    options: [],
    searchKey: "",
  });
  const [loading, setLoading] = useState(false);
  const [anchorElPopover, setAnchorElPopover] = useState(null);
  const clientId = localStorage.getItem("client_id");
  const openPopover = Boolean(anchorElPopover);
  const startIndex = page * rowsPerPage;
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [type, setType] = useState("");
  const handleOpenFilter = (event, filterKey) => {
    setAnchorElFilter(event.currentTarget);
    setOpenFilterModal(true);
    setSelectedFilterData(filterData.columns.find((x) => x.key == filterKey));
  };

  const handleSaveFilter = (
    selectedOptions,
    filterKey,
    sortOrder,
    searchKey,
  ) => {
    onChangeFilter({
      key: filterKey,
      filter: selectedOptions,
      sort: sortOrder,
      searchKey: searchKey,
    });
    handleCloseFilter();
  };

  const handleCloseFilter = () => {
    setAnchorElFilter(null);
    setOpenFilterModal(false);
  };

  const handleViewClick = (item) => {
    console.log(item);
    setOpenViewModal(true);
    handlePopoverClose();
  };

  const handleDelete = async () => {
    try {
      if (type === "shipment") {
        await shipmentService.cancelShipment({
          tms: selectedShipment.tms,
          order: selectedShipment.order,
          shipment: selectedShipment.shipment,
          _id: selectedShipment._id,
          client_id: selectedShipment.client_id,
        });
        toast.success("Shipment deleted successfully");
      } else {
        await shipmentService.cancelLabel({
          tms: selectedShipment.tms,
          response: selectedShipment.response,
          order: selectedShipment.order,
          shipment: selectedShipment.shipment,
          _id: selectedShipment._id,
        });
        toast.success("Label cancelled successfully");
      }
      onDeleteData && onDeleteData();
      setOpenConfirmation(false);
    } catch (e) {
      // toast.error("Failed to process");
      console.error(e);
    }
  };
  const handlePopoverOpen = (event, item) => {
    event.stopPropagation();
    setAnchorElPopover(event.currentTarget);
    setSelectedShipment(item);
  };

  const handlePopoverClose = () => {
    setAnchorElPopover(null);
  };

  const NoDataImage = () => (
    <div
      style={{
        width: "100%",
        justifyContent: "center",
        display: "flex",
      }}
    >
      <img
        src="no_data_rateCard.png"
        alt="No data available"
        style={{ objectFit: "fill", minHeight: "100%" }}
      />
    </div>
  );

  const fetchLabel = async () => {
    setAnchorElPopover(null);

    setLoading(true);
    try {
      let resp = await shipmentService.fetchLabel(selectedShipment);
      setLoading(false);
      toast.success("Label fetched successfully");
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const getNestedValue = (obj, path) => {
    if (!obj || !path) return undefined;
    return path.split(".").reduce((acc, key) => acc?.[key], obj);
  };

  const TableSchema = [
    {
      key: "sl_no",
      title: "SL NO",
      render: (item, index) => (
        <Typography
          fontSize="12px"
          color="#667085"
          fontWeight={400}
          fontFamily={"Poppins"}
        >
          {startIndex + index + 1}
        </Typography>
      ),
    },
    {
      key: "shipment",
      title: "SHIPMENT ID",
      filterKey: "shipment",
      handleOpenFilter: handleOpenFilter,
      displayKey: "shipment",
    },
    {
      key: "order_id",
      title: "ORDER ID",
      filterKey: "order_id",
      handleOpenFilter: handleOpenFilter,
      displayKey: "order_id",
    },
    {
      key: "response.shipment.labels.tracking_number",
      title: "TRACKING NUMBER",
      filterKey: "response.shipment.labels.tracking_number",
      handleOpenFilter: handleOpenFilter,
      render: (x) => (
        <Typography
          fontSize="12px"
          color="#667085"
          fontWeight={400}
          fontFamily={"Poppins"}
        >
          {x?.response?.shipment?.labels?.find((x) => !x.cancelled)
            ?.tracking_number || "--"}
        </Typography>
      ),
    },
    {
      key: "weight",
      title: "WEIGHT",
      render: (x) => (
        <Typography
          fontSize="12px"
          color="#667085"
          fontWeight={400}
          fontFamily={"Poppins"}
        >
          {`${x?.weight}(${x?.weight_unit})` || "--"}
        </Typography>
      ),
    },
    {
      key: "dimension",
      title: "DIMENSION",
      render: (x) => (
        <Typography
          fontSize="12px"
          color="#667085"
          fontWeight={400}
          fontFamily={"Poppins"}
        >
          {`${Math.round(x?.dimension?.length || 0)} * ${Math.round(
            x?.dimension?.width || 0,
          )} * ${Math.round(x?.dimension?.height || 0)} (${
            x?.dimension?.unit
          })`}
        </Typography>
      ),
    },
    {
      key: "overpack",
      title: "OVERPACK",
      displayKey: "overpack",
    },

    {
      key: "manifest",
      title: "MANIFEST",
      displayKey: "manifest",
    },
    {
      key: "manifest_date",
      title: "MANIFEST DATE",
      render: (x) => (
        <Typography
          fontSize="12px"
          color="#667085"
          fontWeight={400}
          fontFamily={"Poppins"}
        >
          {formatDate(x?.manifest_date, "")}
        </Typography>
      ),
    },

    {
      key: "response.shipment.service",
      title: "SERVICE",
      render: (x) => (
        <Typography
          fontSize="12px"
          color="#667085"
          fontWeight={400}
          fontFamily={"Poppins"}
        >
          {x?.response?.shipment?.service}
        </Typography>
      ),
    },
    {
      key: "response.shipment.total_cost",
      title: "TOTAL COST",
      render: (x) => (
        <Typography
          fontSize="12px"
          color="#667085"
          fontWeight={400}
          fontFamily={"Poppins"}
        >
          {x?.response?.shipment?.total_cost}
        </Typography>
      ),
    },
    {
      key: "action",
      title: "",
      width: "3%",
      render: (x) => (
        <Box sx={{ display: "flex", boxShadow: "none" }}>
          <IconButton
            aria-label="more actions"
            onClick={(e) => handlePopoverOpen(e, x)}
            sx={{
              color: openPopover ? "primary" : "",
            }}
          >
            <MoreVertIcon />
          </IconButton>
          <Popover
            open={Boolean(anchorElPopover)}
            anchorEl={anchorElPopover}
            onClose={handlePopoverClose}
            anchorOrigin={{
              vertical: isMobile() ? "bottom" : "top",
              horizontal: isMobile() ? "center" : "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            sx={{
              "& .MuiPaper-root": {
                boxShadow: "none",
                border: "1px solid #EAECF0",
                borderRadius: "4px",
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                padding: "10px",
                boxShadow: "none",
                gap: 2,
              }}
            >
              <Button
                style={{
                  color: "#333333",
                  justifyContent: "flex-start",
                  textTransform: "none",
                }}
                onClick={() => handleViewClick(x)}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="flex-start"
                  width="100%"
                  gap={1}
                >
                  <ViewIcon />
                  <Typography
                    fontFamily={"poppins"}
                    fontSize={"16px"}
                    color={"#333333"}
                    fontWeight={500}
                  >
                    View
                  </Typography>
                </Box>
              </Button>

              {Array.isArray(selectedShipment?.response?.shipment?.labels) &&
                selectedShipment?.response.shipment.labels.length > 0 &&
                !selectedShipment?.response.shipment.labels.some(
                  (item) => item.cancelled,
                ) && (
                  <Button
                    style={{
                      justifyContent: "flex-start",
                      textTransform: "none",
                    }}
                    onClick={() => fetchLabel()}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="flex-start"
                      width="100%"
                      gap={1}
                    >
                      <RefreshOutlined />
                      <Typography
                        fontFamily="poppins"
                        fontSize="16px"
                        color="#000"
                        fontWeight={500}
                      >
                        Fetch Label
                      </Typography>
                    </Box>
                  </Button>
                )}

              {Array.isArray(selectedShipment?.response?.shipment?.labels) &&
                selectedShipment?.response.shipment.labels.length > 0 &&
                !selectedShipment?.response.shipment.labels.some(
                  (item) => item.cancelled,
                ) && (
                  <Button
                    style={{
                      justifyContent: "flex-start",
                      textTransform: "none",
                      cursor:
                        selectedShipment?.overpack ||
                        selectedShipment?.response?.shipment?.labels.find(
                          (x) => x.cancelled,
                        )
                          ? "not-allowed"
                          : "pointer",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setType("label");
                      if (
                        selectedShipment?.response?.shipment?.labels.find(
                          (x) => !x.cancelled,
                        ) &&
                        !selectedShipment?.overpack
                      ) {
                        setOpenConfirmation(true);
                      }
                    }}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="flex-start"
                      width="100%"
                      gap={1}
                    >
                      <DeleteIcon
                        fill={
                          selectedShipment?.overpack ||
                          selectedShipment?.response?.shipment?.labels.find(
                            (x) => x.cancelled,
                          )
                            ? "grey"
                            : null
                        }
                      />
                      <Typography
                        fontFamily={"poppins"}
                        fontSize={"16px"}
                        color={
                          selectedShipment?.overpack ||
                          selectedShipment?.response?.shipment?.labels.find(
                            (x) => x.cancelled,
                          )
                            ? "grey"
                            : primary_color
                        }
                        fontWeight={500}
                      >
                        Cancel Label
                      </Typography>
                    </Box>
                  </Button>
                )}
              <Button
                style={{
                  // color: "#333333",
                  justifyContent: "flex-start",
                  textTransform: "none",
                  cursor: selectedShipment?.overpack
                    ? "not-allowed"
                    : "pointer",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setType("shipment");
                  if (!selectedShipment?.overpack) {
                    setOpenConfirmation(true);
                  }
                }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="flex-start"
                  width="100%"
                  gap={1}
                >
                  <DeleteIcon
                    fill={selectedShipment?.overpack ? "grey" : null}
                  />
                  <Typography
                    fontFamily={"poppins"}
                    fontSize={"16px"}
                    color={selectedShipment?.overpack ? "grey" : primary_color}
                    fontWeight={500}
                  >
                    Delete Shipment
                  </Typography>
                </Box>
              </Button>
            </Box>
          </Popover>
        </Box>
      ),
    },
  ];

  const MobileCardView = ({ data }) => {
    return (
      <Box sx={{ width: "100%", padding: 0.5 }}>
        {data.length === 0 ? (
          <NoDataImage />
        ) : (
          data?.map((x, index) => (
            <Card
              key={index}
              sx={{
                marginBottom: 2,
                backgroundColor: "#ffff",
                borderRadius: 4,
              }}
            >
              <CardContent>
                <div style={{ width: "100%" }}>
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="flex-start"
                  >
                    <Grid container spacing={2} sx={{ marginTop: 1 }}>
                      <Grid item xs={6}>
                        <Typography
                          fontSize={14}
                          fontFamily="poppins"
                          color="#333333"
                          fontWeight={600}
                          textAlign="left"
                        >
                          Leg
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography
                          fontSize={14}
                          fontFamily="Poppins"
                          color="#606060"
                          fontWeight={500}
                          noWrap
                          overflow="hidden"
                          textOverflow="ellipsis"
                          textAlign="left"
                        >
                          : {x?.leg}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography
                          fontSize={14}
                          fontFamily="poppins"
                          color="##333333"
                          fontWeight={600}
                          textAlign="left"
                        >
                          Code
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography
                          fontSize={14}
                          fontFamily="Poppins"
                          color="#606060"
                          fontWeight={500}
                          noWrap
                          overflow="hidden"
                          textOverflow="ellipsis"
                          textAlign="left"
                        >
                          : {x?.code}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography
                          fontSize={14}
                          fontFamily="poppins"
                          color="##333333"
                          fontWeight={600}
                          textAlign="left"
                        >
                          Description
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography
                          fontSize={14}
                          fontFamily="Poppins"
                          color="#606060"
                          fontWeight={500}
                          noWrap
                          overflow="hidden"
                          textOverflow="ellipsis"
                          textAlign="left"
                        >
                          : {x?.description}
                        </Typography>
                      </Grid>
                      {user.role == "admin" ? (
                        <Grid item xs={6}>
                          <Typography
                            fontSize={14}
                            fontFamily="poppins"
                            color="##333333"
                            fontWeight={600}
                            textAlign="left"
                          >
                            Optional
                          </Typography>
                        </Grid>
                      ) : null}
                      {user.role == "admin" ? (
                        <Grid item xs={6}>
                          <Typography
                            fontSize={14}
                            fontFamily="Poppins"
                            color="#606060"
                            fontWeight={500}
                            noWrap
                            overflow="hidden"
                            textOverflow="ellipsis"
                            textAlign="left"
                          >
                            : {x?.optional == true ? "Yes" : "No"}
                          </Typography>
                        </Grid>
                      ) : (
                        <> </>
                      )}
                      {user.role == "admin" ? (
                        <Grid item xs={6}>
                          <Typography
                            fontSize={14}
                            fontFamily="poppins"
                            color="##333333"
                            fontWeight={600}
                            textAlign="left"
                          >
                            Sysytem Generated
                          </Typography>
                        </Grid>
                      ) : (
                        <></>
                      )}
                      {user.role == "admin" ? (
                        <Grid item xs={6}>
                          <Typography
                            fontSize={14}
                            fontFamily="Poppins"
                            color="#606060"
                            fontWeight={500}
                            noWrap
                            overflow="hidden"
                            textOverflow="ellipsis"
                            textAlign="left"
                          >
                            : {x?.system_generated == true ? "Yes" : "No"}
                          </Typography>
                        </Grid>
                      ) : (
                        <></>
                      )}
                    </Grid>
                  </Box>

                  <Box mt={2} justifyContent={"flex-end"} display="flex">
                    {x?.action}
                  </Box>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </Box>
    );
  };

  const renderListView = () => (
    <div
      style={{
        width: "100%",
        overflow: isMobile() ? "auto" : "hidden",
        // paddingBottom: 20,
        marginTop: 5,
      }}
    >
      {isMobile() ? (
        <MobileCardView data={data} />
      ) : (
        <TableContainer
          sx={{
            overflowY: "auto",
            paddingInline: 0.4,
            maxHeight: "100%",
          }}
        >
          <Table
            stickyHeader
            aria-label="sticky table"
            sx={{
              border: "1px solid var(--border-color)",
            }}
          >
            <TableHead>
              <StyledTableRow>
                {TableSchema.map((column) => (
                  <TableHeader
                    key={column.key}
                    column={column}
                    filterData={filterData}
                    filterKey={column.filterKey}
                    handleOpenFilter={column.handleOpenFilter}
                    title={column.title}
                  />
                ))}
              </StyledTableRow>
            </TableHead>
            <TableBody>
              {data.length === 0 ? (
                <StyledTableRow>
                  <TableCell colSpan={8} align="center">
                    <NoDataImage />
                  </TableCell>
                </StyledTableRow>
              ) : (
                data?.map((x, rowIndex) => (
                  <StyledTableRow key={rowIndex}>
                    {TableSchema.map((column, colIndex) => (
                      <StyledTableCell
                        key={column.key || colIndex}
                        sx={{ width: column.width }}
                      >
                        {column.displayKey ? (
                          <Typography
                            fontSize="12px"
                            color="#667085"
                            fontWeight={400}
                            fontFamily={"Poppins"}
                          >
                            {getNestedValue(x, column.displayKey) || "--"}
                          </Typography>
                        ) : column.render ? (
                          column.render(x, rowIndex)
                        ) : (
                          "--"
                        )}
                      </StyledTableCell>
                    ))}
                  </StyledTableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </div>
  );
  return (
    <>
      <div sx={{ minWidth: "100%" }}>
        <div
          style={{
            overflowY: "scroll",
            display: "flex",
            maxHeight: isMobile() ? "60vh" : `calc(100vh - 210px)`,
            width: "100%",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            "&::WebkitScrollbar": { width: 0 },
          }}
        >
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                zIndex: 9999,
                color: "white",
              }}
            >
              <CircularProgress color="inherit" />
            </Box>
          ) : (
            renderListView()
          )}
        </div>

        <Pagination
          dataCount={dataCount}
          rowsPerPage={rowsPerPage}
          page={page}
          setPage={setPage}
          setRowsPerPage={setRowsPerPage}
          disableNext={dataCount <= (page + 1) * filterData.limit}
        />
        <ShipmentViewModal
          data={selectedShipment}
          open={openViewModal}
          onClose={() => {
            setOpenViewModal(false);
          }}
          primary_color={primary_color}
        />
        <DeleteConfirmationModal
          title={`Are you sure you want to  ${
            type == "shipment" ? "delete shipment" : "cancel label"
          } ?`}
          open={openConfirmation}
          handleClose={() => setOpenConfirmation(false)}
          handleDelete={(e) => {
            handleDelete();
          }}
          deleteButtonTitle={type == "shipment" ? "Delete" : "Okay"}
        />
        <Popover
          open={Boolean(anchorElFilter)}
          anchorEl={anchorElFilter}
          onClose={handleCloseFilter}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          disableScrollLock={true}
        >
          <FilterModal
            open={openFilterModal}
            onClose={handleCloseFilter}
            options={selectedfilterData?.options}
            selectedValues={selectedfilterData?.filter}
            onSave={handleSaveFilter}
            currentSortOrder={selectedfilterData?.sort}
            filterKey={selectedfilterData?.key}
            hasMore={selectedfilterData?.loadMoreButton}
            displayKey={selectedfilterData?.displayKey}
            id={selectedfilterData?.id}
            fetchApi={selectedfilterData?.fetchApi}
            searchKey={selectedfilterData?.searchKey}
          />
        </Popover>
      </div>
    </>
  );
};

export default Shipments;
